/**
 * PreviewTab — standalone full-width preview (legacy entry; split panel is primary).
 */

import { FC } from 'react';
import { matchTracker } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';

import PreviewBinomSection from './PreviewBinomSection';
import PreviewVoluumSection from './PreviewVoluumSection';
import PreviewLiveBadge from '../components/preview/PreviewLiveBadge';
import { useFlowOptimizerLivePreview } from '../context/FlowOptimizerLivePreviewContext';
import type { FlowOptimizerProvider } from '../services/flow-optimizer-api';

export interface PreviewTabProps {
  provider?: FlowOptimizerProvider;
  killSwitch: boolean;
}

const PreviewTab: FC<PreviewTabProps> = ({
  provider = 'voluum',
  killSwitch,
}) => {
  const { live, result, applied, applyPending, onApply } =
    useFlowOptimizerLivePreview();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <PreviewLiveBadge isFetching={live.isFetching} isStale={live.isStale} />
        <Button
          variant="default"
          type="button"
          onClick={onApply}
          disabled={live.isFetching || applyPending || killSwitch}
          loading={applyPending}
        >
          Apply
        </Button>
      </div>
      {live.error ? (
        <p className="text-sm text-destructive">Preview failed: {live.error.message}</p>
      ) : null}
      {live.isFetching && !result ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      ) : result ? (
        matchTracker(provider, {
          binom: () => (
            <PreviewBinomSection result={result} applied={applied} killSwitch={killSwitch} />
          ),
          // Tres Commas flows are Voluum-shaped: same Standard workspace.
          voluum: () => <PreviewVoluumSection result={result} applied={applied} />,
          'tres-commas': () => <PreviewVoluumSection result={result} applied={applied} />,
        })
      ) : (
        <p className="text-xs italic text-muted-foreground">
          No preview yet — edit Rules or Formulas to refresh.
        </p>
      )}
    </div>
  );
};

export default PreviewTab;
